import React from "react";

/**
 * Installer branding config — mirrors /frontend/src/branding/installer.ts on mobile.
 * Rebranding for another installer requires ONLY editing this file.
 */
export const installerBranding = {
    name: "Pearl Energy",
    tagline: "Invest in saving money",
    logoUrl: "/pearl-energy-logo.png",
    logoAspectRatio: 2.0,           // approx 2:1 wide logo
    accentColor: "#3E9E2E",         // Pearl Energy green — the primary EnergyFlow brand colour
    website: "https://pearlenergy.co.uk",
    phone: "+44 1522 123 456",
    email: "hello@pearlenergy.co.uk",
};

export const appConfig = {
    name: "Pearl - EnergyFlow",
    tagline: "Your Solar Installation Journey",
};

/**
 * Pearl Energy installer logo — scales width from a target height, keeps 2:1 ratio.
 * `variant="dark"` reduces opacity slightly for placement over navy hero backgrounds.
 */
export function InstallerLogo({ height = 32, variant = "light", className = "", testid }) {
    const width = height * installerBranding.logoAspectRatio;
    return (
        <img
            src={installerBranding.logoUrl}
            alt={`${installerBranding.name} logo`}
            data-testid={testid || "installer-logo"}
            style={{ width, height, objectFit: "contain", opacity: variant === "dark" ? 0.95 : 1 }}
            className={className}
        />
    );
}

/**
 * "Powered by EnergyFlow" strap — used on splash / auth surfaces.
 * DEV-012C · Product name presented to the user is "Pearl - EnergyFlow". The
 * underlying-platform attribution is "Powered by EnergyFlow".
 */
export function PoweredByInstaller({ align = "center", logoHeight = 40, className = "" }) {
    return (
        <div className={`flex flex-col ${align === "center" ? "items-center" : "items-start"} gap-2 ${className}`}>
            <span className="text-[12px] tracking-[0.04em] text-white/70">Powered by EnergyFlow</span>
            <InstallerLogo height={logoHeight} variant="dark" />
        </div>
    );
}

/**
 * Small "Installed by Pearl Energy" line — for the customer dashboard.
 */
export function InstalledByInstaller({ className = "" }) {
    return (
        <div className={`flex items-center gap-2 ${className}`} data-testid="installed-by-installer">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: installerBranding.accentColor }} />
            <span className="text-sm text-[#4B5565]">
                Installed by <span className="text-[#131B2F] font-medium">{installerBranding.name}</span>
            </span>
        </div>
    );
}

/**
 * Compact identity header — used in the installer console.
 */
export function InstallerIdentityHeader({ className = "" }) {
    return (
        <div
            className={`flex items-center gap-3 p-3 bg-white rounded-[20px] border border-[#E5E7EB] ${className}`}
            data-testid="installer-identity-header"
        >
            <InstallerLogo height={44} />
            <div className="flex-1 min-w-0">
                <div className="text-base text-[#131B2F] font-medium truncate">{installerBranding.name}</div>
                <div className="text-sm text-[#4B5565] truncate">{installerBranding.tagline}</div>
            </div>
            <span className="w-1 h-8 rounded-sm" style={{ background: installerBranding.accentColor }} />
        </div>
    );
}

/**
 * Contact block — shown on About and Profile screens.
 * Mirrors InstallerContactBlock on mobile.
 */
export function InstallerContactBlock({ className = "" }) {
    const rows = [
        { label: "Website", value: installerBranding.website, href: installerBranding.website },
        { label: "Phone",   value: installerBranding.phone,   href: `tel:${installerBranding.phone.replace(/\s+/g, "")}` },
        { label: "Email",   value: installerBranding.email,   href: `mailto:${installerBranding.email}` },
    ];
    return (
        <div className={`space-y-3 ${className}`} data-testid="installer-contact-block">
            {rows.map((r, i) => (
                <div key={r.label} className={`flex items-center justify-between ${i > 0 ? "pt-3 border-t border-[#E5E7EB]" : ""}`}>
                    <span className="text-sm text-[#4B5565]">{r.label}</span>
                    <a
                        href={r.href}
                        target={r.label === "Website" ? "_blank" : undefined}
                        rel="noreferrer"
                        className="text-sm text-[#131B2F] font-medium hover:text-[#3E9E2E] transition-colors"
                    >
                        {r.value}
                    </a>
                </div>
            ))}
        </div>
    );
}
